import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Btn, Card, Badge, EmptyState } from "@/components/ui-kit";
import { CalendarCheck, Users2, MapPin, TrendingUp, Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PURPOSE_LABEL = {
  new_customer: "New Customer", follow_up: "Follow-up", payment_collection: "Payment",
  order_taking: "Order", product_detailing: "Detailing", sampling: "Sampling",
  complaint: "Complaint", market_survey: "Survey", stock_check: "Stock", other: "Other",
};

const PURPOSE_COLOR = {
  new_customer: "purple", follow_up: "blue", payment_collection: "amber",
  order_taking: "green", product_detailing: "blue", sampling: "purple",
  complaint: "red", market_survey: "slate", stock_check: "slate", other: "slate",
};

export default function PlanDashboard() {
  const { user } = useAuth();
  const has = (p) => user?.permissions?.includes(p);
  const isSupervisor = has("plans.view_all");
  const [tab, setTab] = useState(isSupervisor ? "team" : "mine");
  const [mine, setMine] = useState(null);
  const [team, setTeam] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    if (tab === "mine") {
      try { const { data } = await api.get("/dashboard/my-plan"); setMine(data); }
      catch (e) { console.error(e); }
    } else {
      try { const { data } = await api.get("/dashboard/team-plans"); setTeam(data); }
      catch (e) { console.error(e); }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  return (
    <div data-testid="plan-dashboard">
      <PageHeader
        title="Plan Dashboard"
        subtitle={isSupervisor ? "Team plan oversight + your own progress" : "Your weekly visit plan progress"}
        actions={
          <Btn variant="secondary" onClick={() => nav("/plans")} data-testid="dashboard-go-plans">
            <CalendarCheck className="w-4 h-4" /> Manage Plans
          </Btn>
        }
      />

      {isSupervisor && (
        <div className="mb-4 flex gap-2">
          <button onClick={() => setTab("team")} className={`px-3 py-1.5 rounded text-sm ${tab === "team" ? "bg-blue-700 text-white" : "bg-slate-100 dark:bg-slate-800"}`} data-testid="dashboard-tab-team">
            <Users2 className="w-4 h-4 inline-block me-1" /> Team
          </button>
          <button onClick={() => setTab("mine")} className={`px-3 py-1.5 rounded text-sm ${tab === "mine" ? "bg-blue-700 text-white" : "bg-slate-100 dark:bg-slate-800"}`}>
            <CalendarCheck className="w-4 h-4 inline-block me-1" /> Mine
          </button>
        </div>
      )}

      {tab === "mine" && <MyPlanView data={mine} />}
      {tab === "team" && <TeamPlanView data={team} onJump={(id) => nav("/plans")} />}
    </div>
  );
}

function MyPlanView({ data }) {
  if (!data) return <EmptyState message="Loading..." />;
  const { plan, plan_stats, today_targets = [], visits_by_purpose = {}, visits_per_day = {}, top_targets_visited = [], this_week_visits_total = 0, daily_target = 9, today, week_start } = data;

  return (
    <div className="space-y-6">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="text-xs uppercase text-slate-500 tracking-wider">Week of</div>
          <div className="font-bold text-xl mt-1">{week_start}</div>
          <div className="text-xs text-slate-400 mt-1">Today: {today}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase text-slate-500 tracking-wider">Plan Status</div>
          <div className="mt-2">
            {plan ? <Badge color={plan.status === "approved" ? "green" : plan.status === "pending_approval" ? "amber" : "slate"}>{plan.status.replace("_", " ")}</Badge> : <Badge color="red">No Plan</Badge>}
          </div>
          {plan_stats && (
            <div className="text-xs text-slate-500 mt-2">{plan_stats.completed_targets}/{plan_stats.total_targets} done ({plan_stats.completion_pct}%)</div>
          )}
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase text-slate-500 tracking-wider">Visits This Week</div>
          <div className="font-bold text-3xl text-blue-700 mt-1">{this_week_visits_total}</div>
          <div className="text-xs text-slate-500 mt-1">Daily target: {daily_target}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase text-slate-500 tracking-wider">Today's Targets</div>
          <div className="font-bold text-3xl text-emerald-700 mt-1">{today_targets.length}</div>
          <div className="text-xs text-slate-500 mt-1">{today_targets.filter((t) => t.completed).length} completed</div>
        </Card>
      </div>

      {/* Today's plan */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3"><CalendarCheck className="w-5 h-5 text-blue-700" /><h2 className="font-bold">Today's Plan</h2></div>
        {today_targets.length === 0 ? <EmptyState message="No targets planned for today" /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {today_targets.map((t, i) => (
              <div key={t.id || i} className={`rounded p-3 text-sm ring-1 ${t.completed ? "ring-emerald-200 bg-emerald-50 dark:bg-emerald-950/30" : "ring-slate-200 dark:ring-slate-700"}`} data-testid={`today-target-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{t.target_name}</div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-1 text-xs">
                      <Badge color={t.target_type === "doctor" ? "purple" : "blue"}>{t.target_type}</Badge>
                      {t.classification && <Badge color="slate">{t.classification}</Badge>}
                      {t.planned_time && <span className="text-slate-500">🕐 {t.planned_time}</span>}
                    </div>
                    <div className="mt-1.5"><Badge color={PURPOSE_COLOR[t.visit_purpose]}>{PURPOSE_LABEL[t.visit_purpose]}</Badge></div>
                  </div>
                  {t.completed && <Activity className="w-4 h-4 text-emerald-600" />}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="p-4">
          <h3 className="font-bold mb-3 text-sm uppercase tracking-wider text-slate-500">Visits by Purpose</h3>
          {Object.keys(visits_by_purpose).length === 0 ? <div className="text-xs text-slate-400">No visits this week</div> : (
            <div className="space-y-2">
              {Object.entries(visits_by_purpose).sort((a, b) => b[1] - a[1]).map(([k, v]) => {
                const max = Math.max(...Object.values(visits_by_purpose));
                return (
                  <div key={k}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span><Badge color={PURPOSE_COLOR[k]}>{PURPOSE_LABEL[k] || k}</Badge></span>
                      <span className="font-mono font-semibold">{v}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800"><div className="h-full rounded-full bg-blue-500" style={{ width: `${(v / max) * 100}%` }}/></div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
        <Card className="p-4">
          <h3 className="font-bold mb-3 text-sm uppercase tracking-wider text-slate-500">Visits Per Day</h3>
          {Object.keys(visits_per_day).length === 0 ? <div className="text-xs text-slate-400">No visits this week</div> : (
            <div className="space-y-2">
              {Object.entries(visits_per_day).sort().map(([day, count]) => {
                const pct = (count / daily_target) * 100;
                return (
                  <div key={day}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span>{day}</span>
                      <span className={`font-mono font-semibold ${count >= daily_target ? "text-emerald-600" : "text-amber-600"}`}>{count} / {daily_target}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800"><div className={`h-full rounded-full ${count >= daily_target ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.min(pct, 100)}%` }}/></div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-4">
        <h3 className="font-bold mb-3 text-sm uppercase tracking-wider text-slate-500">Top Targets Visited This Week</h3>
        {top_targets_visited.length === 0 ? <div className="text-xs text-slate-400">No visits</div> : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {top_targets_visited.map((t) => (
                <tr key={t.target_id}>
                  <td className="py-2 font-medium">{t.target_name}</td>
                  <td className="py-2 text-end"><Badge color="blue">{t.count}× visited</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function TeamPlanView({ data, onJump }) {
  if (!data) return <EmptyState message="Loading..." />;
  const { pending_approvals = [], pending_count, this_week_per_rep = [], visits_by_purpose = {}, visit_heatmap = [], rep_leaderboard = [], total_visits_this_week, week_start } = data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="text-xs uppercase text-slate-500">Week</div>
          <div className="font-bold text-xl mt-1">{week_start}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase text-slate-500">Pending Approval</div>
          <div className="font-bold text-3xl text-amber-600 mt-1">{pending_count}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase text-slate-500">Reps Active</div>
          <div className="font-bold text-3xl text-blue-700 mt-1">{this_week_per_rep.length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase text-slate-500">Visits This Week</div>
          <div className="font-bold text-3xl text-emerald-700 mt-1">{total_visits_this_week}</div>
        </Card>
      </div>

      {pending_approvals.length > 0 && (
        <Card className="p-4">
          <h3 className="font-bold mb-3 text-sm uppercase tracking-wider text-amber-600">⏳ Plans Awaiting Your Approval</h3>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase"><tr><th className="text-start py-1">Rep</th><th className="text-start">Week</th><th className="text-end">Targets</th><th className="text-start">Submitted</th><th></th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {pending_approvals.map((p) => (
                <tr key={p.id} data-testid={`pending-${p.id}`}>
                  <td className="py-2 font-medium">{p.rep_name}</td>
                  <td className="py-2">{p.week_start}</td>
                  <td className="py-2 text-end font-mono">{p.total_targets}</td>
                  <td className="py-2 text-xs text-slate-500">{new Date(p.submitted_at).toLocaleString()}</td>
                  <td className="py-2 text-end"><Btn className="!py-1 !px-2 !text-xs" onClick={() => onJump(p.id)}>Review</Btn></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4">
          <h3 className="font-bold mb-3 text-sm uppercase tracking-wider text-slate-500"><TrendingUp className="w-4 h-4 inline-block me-1" /> Rep Leaderboard (this week)</h3>
          {rep_leaderboard.length === 0 ? <div className="text-xs text-slate-400">No visits</div> : (
            <table className="w-full text-sm">
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {rep_leaderboard.map((r, i) => (
                  <tr key={r.rep_id}>
                    <td className="py-2"><span className="text-slate-400 me-2">#{i + 1}</span><span className="font-medium">{r.rep_name}</span></td>
                    <td className="py-2 text-end font-mono font-semibold">{r.visit_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
        <Card className="p-4">
          <h3 className="font-bold mb-3 text-sm uppercase tracking-wider text-slate-500">Team Visits by Purpose</h3>
          {Object.keys(visits_by_purpose).length === 0 ? <div className="text-xs text-slate-400">No visits</div> : (
            <div className="space-y-2">
              {Object.entries(visits_by_purpose).sort((a, b) => b[1] - a[1]).map(([k, v]) => {
                const max = Math.max(...Object.values(visits_by_purpose));
                return (
                  <div key={k}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span><Badge color={PURPOSE_COLOR[k]}>{PURPOSE_LABEL[k] || k}</Badge></span>
                      <span className="font-mono font-semibold">{v}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800"><div className="h-full rounded-full bg-blue-500" style={{ width: `${(v / max) * 100}%` }}/></div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-4">
        <h3 className="font-bold mb-3 text-sm uppercase tracking-wider text-slate-500">Plan Progress per Rep</h3>
        {this_week_per_rep.length === 0 ? <div className="text-xs text-slate-400">No plans this week</div> : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase border-b border-slate-100 dark:border-slate-800"><tr><th className="text-start py-1">Rep</th><th className="text-start">Status</th><th className="text-end">Targets</th><th className="text-end">Done</th><th className="text-end">%</th><th></th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {this_week_per_rep.map((p) => (
                <tr key={p.rep_id}>
                  <td className="py-2 font-medium">{p.rep_name}</td>
                  <td className="py-2"><Badge color={p.status === "approved" ? "green" : p.status === "pending_approval" ? "amber" : "slate"}>{p.status.replace("_", " ")}</Badge></td>
                  <td className="py-2 text-end font-mono">{p.total_targets}</td>
                  <td className="py-2 text-end font-mono text-emerald-600">{p.completed_targets}</td>
                  <td className="py-2 text-end">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${p.completion_pct}%` }}/></div>
                      <span className="text-xs font-mono">{p.completion_pct}%</span>
                    </div>
                  </td>
                  <td className="py-2 text-end"><Btn className="!py-1 !px-2 !text-xs" variant="secondary" onClick={() => onJump(p.plan_id)}>View</Btn></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="font-bold mb-3 text-sm uppercase tracking-wider text-slate-500"><MapPin className="w-4 h-4 inline-block me-1" /> Most-Visited Targets (this week)</h3>
        {visit_heatmap.length === 0 ? <div className="text-xs text-slate-400">No visits</div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {visit_heatmap.slice(0, 12).map((t) => (
              <div key={t.target_id} className="rounded ring-1 ring-slate-200 dark:ring-slate-700 p-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{t.target_name}</div>
                    <Badge color={t.target_type === "doctor" ? "purple" : "blue"}>{t.target_type}</Badge>
                  </div>
                  <Badge color="blue">{t.count}×</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
